import { useLocation, Link } from "react-router-dom";
import { useEffect } from "react";
import { Home, ArrowLeft, Sparkles, Compass } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SEO } from "@/components/SEO";

const NotFound = () => {
  const location = useLocation();

  useEffect(() => {
    console.error("404 Error: User attempted to access non-existent route:", location.pathname);
  }, [location.pathname]);

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-background via-background to-primary/5 flex items-center justify-center px-4">
      <SEO
        title="Página no encontrada — LÍDER IA"
        description="La página que buscas no existe o fue movida."
        path={location.pathname}
        noindex
      />
      {/* Animated background blobs */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-24 -left-24 h-96 w-96 rounded-full bg-primary/20 blur-3xl animate-pulse" />
        <div
          className="absolute -bottom-24 -right-24 h-96 w-96 rounded-full bg-accent/20 blur-3xl animate-pulse"
          style={{ animationDelay: "1s" }}
        />
        <div
          className="absolute top-1/2 left-1/2 h-72 w-72 -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary/10 blur-3xl animate-pulse"
          style={{ animationDelay: "2s" }}
        />
      </div>

      {/* Floating sparkles */}
      <Sparkles
        className="absolute top-20 left-[15%] h-6 w-6 text-primary/60 animate-bounce"
        style={{ animationDuration: "3s" }}
      />
      <Sparkles
        className="absolute top-32 right-[18%] h-4 w-4 text-accent/60 animate-bounce"
        style={{ animationDuration: "4s", animationDelay: "0.5s" }}
      />
      <Sparkles
        className="absolute bottom-32 left-[20%] h-5 w-5 text-primary/50 animate-bounce"
        style={{ animationDuration: "3.5s", animationDelay: "1s" }}
      />

      <div className="relative z-10 text-center max-w-2xl mx-auto animate-fade-in">
        {/* Animated compass */}
        <div className="mb-8 flex justify-center">
          <div className="relative">
            <div className="absolute inset-0 rounded-full bg-gradient-to-br from-primary to-accent blur-xl opacity-50 animate-pulse" />
            <div className="relative h-24 w-24 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center shadow-2xl">
              <Compass
                className="h-12 w-12 text-primary-foreground"
                style={{ animation: "spin 8s linear infinite" }}
              />
            </div>
          </div>
        </div>

        {/* Big 404 with gradient */}
        <h1 className="text-[8rem] md:text-[12rem] font-black leading-none tracking-tighter bg-gradient-to-br from-primary via-primary to-accent bg-clip-text text-transparent select-none">
          404
        </h1>

        <h2 className="text-2xl md:text-4xl font-bold text-foreground mb-4 -mt-4">
          ¡Ups! Te perdiste en el camino
        </h2>

        <p className="text-base md:text-lg text-muted-foreground mb-2 max-w-md mx-auto">
          La página que buscas no existe o fue movida a otro lugar.
        </p>
        <p className="text-sm text-muted-foreground/70 mb-8 font-mono">
          {location.pathname}
        </p>

        {/* CTAs */}
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Button asChild size="lg" className="group shadow-lg hover:shadow-xl transition-all">
            <Link to="/">
              <Home className="h-4 w-4 mr-2 transition-transform group-hover:scale-110" />
              Volver al inicio
            </Link>
          </Button>
          <Button
            variant="outline"
            size="lg"
            onClick={() => window.history.back()}
            className="group"
          >
            <ArrowLeft className="h-4 w-4 mr-2 transition-transform group-hover:-translate-x-1" />
            Página anterior
          </Button>
        </div>

        {/* Decorative bottom line */}
        <div className="mt-12 flex items-center justify-center gap-2 text-xs text-muted-foreground/60">
          <div className="h-px w-12 bg-gradient-to-r from-transparent to-muted-foreground/30" />
          <span>LÍDER IA</span>
          <div className="h-px w-12 bg-gradient-to-l from-transparent to-muted-foreground/30" />
        </div>
      </div>
    </div>
  );
};

export default NotFound;
